/**
 * Action Dropdown for timesheet entries
 *
 * Context menu with timer, edit, admin, and delete actions.
 */

import { memo } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  MoreHorizontal,
  Play,
  Square,
  Lock,
  Unlock,
  CheckCircle,
  Copy,
  Trash,
} from 'lucide-react';
import type { TimesheetEntry } from '@/types/timesheet';

export interface ActionDropdownProps {
  entry: TimesheetEntry;
  onEdit: (field: keyof TimesheetEntry, value: unknown) => void;
  onDelete: () => void;
  onStartTimer: () => void;
  onStopTimer: () => void;
  onLock: () => void;
  onUnlock: () => void;
  onApprove: () => void;
  onDuplicate: () => void;
  canEdit: boolean;
  canManage: boolean;
  isAdmin: boolean;
}

export const ActionDropdown = memo(function ActionDropdown({
  entry,
  onDelete,
  onStartTimer,
  onStopTimer,
  onLock,
  onUnlock,
  onApprove,
  onDuplicate,
  canEdit,
  canManage,
  isAdmin,
}: ActionDropdownProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 w-8 p-0">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {/* Timer Actions */}
        {entry.id && !entry.isNew && (
          <>
            {entry.timer_active ? (
              <DropdownMenuItem onClick={onStopTimer}>
                <Square className="h-4 w-4 mr-2" />
                Stop Timer
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={onStartTimer} disabled={!canEdit}>
                <Play className="h-4 w-4 mr-2" />
                Start Timer
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
          </>
        )}

        {/* Edit Actions */}
        <DropdownMenuItem onClick={onDuplicate}>
          <Copy className="h-4 w-4 mr-2" />
          Duplicate
        </DropdownMenuItem>

        {/* Admin Actions */}
        {canManage && entry.id && !entry.isNew && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onApprove} disabled={entry.reviewed || !canEdit}>
              <CheckCircle className="h-4 w-4 mr-2" />
              {entry.reviewed ? 'Approved' : 'Approve'}
            </DropdownMenuItem>

            {isAdmin && (
              <>
                {entry.locked ? (
                  <DropdownMenuItem onClick={onUnlock}>
                    <Unlock className="h-4 w-4 mr-2" />
                    Unlock
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={onLock} disabled={entry.timer_active}>
                    <Lock className="h-4 w-4 mr-2" />
                    Lock
                  </DropdownMenuItem>
                )}
              </>
            )}
          </>
        )}

        {/* Delete Action */}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onDelete}
          disabled={!canEdit}
          className="text-destructive focus:text-destructive"
        >
          <Trash className="h-4 w-4 mr-2" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
